import { useMemo, useState } from 'react';
import { BARTER_PRICES } from '../game/constants.js';
import './KenHouseScene.css';

const money = (value) => `$${Math.max(0, Math.floor(Number(value) || 0)).toLocaleString()}`;
const grams = (value) => `${Number(value || 0).toFixed(1).replace(/\.0$/, '')}g`;

function DealButton({ children, disabled, onClick, detail, tone = '' }) {
  return (
    <button className={`ken-choice ${tone}`.trim()} disabled={disabled} onClick={onClick}>
      <strong>{children}</strong>
      {detail && <small>{detail}</small>}
    </button>
  );
}

export function KenHouseScene({ gameState, busy, actions }) {
  const [screen, setScreen] = useState('main');
  const ken = gameState.kenHouse || {};
  const rep = Number(gameState.streetRep || 0);
  const unlocked = rep >= Number(ken.repRequirement || 70);
  const bud = Number(gameState.inventory?.bud || 0);
  const cash = Number(gameState.cash || 0);
  const junk = useMemo(
    () => Object.entries(gameState.barterInventory || {}).filter(([, count]) => Number(count) > 0),
    [gameState.barterInventory],
  );
  const methOptions = [1, 3.5, 7, 28].filter((amount) => amount <= Number(ken.methStock || 0));
  const budOptions = [1, 3.5, 7, 28].filter((amount) => amount <= bud && amount <= Number(ken.weedDemand || 0));

  let title = "Ken the Tweaker's House";
  let copy = "Ken operates a surprisingly organized business from a yard full of dead cars. His wife is yelling from somewhere inside the house.";
  let choices = (
    <>
      <DealButton disabled={busy} onClick={() => setScreen('weed')} detail={`${money(ken.weedOffer)} per gram · demand ${grams(ken.weedDemand)}`}>Sell Ken some weed</DealButton>
      <DealButton disabled={busy} onClick={() => setScreen('meth')} detail={unlocked ? `${money(ken.methPrice)} per gram · stock ${grams(ken.methStock)}` : `Requires ${ken.repRequirement || 70} reputation`}>Buy cheap meth</DealButton>
      <DealButton disabled={busy || junk.length === 0} onClick={() => setScreen('junk')} detail="Ken converts questionable electronics into meth">Trade junk for meth</DealButton>
      <DealButton disabled={busy} onClick={actions.kenLookAround} detail={ken.wifeFlavor || 'Survey the domestic wreckage'}>Look around</DealButton>
      <DealButton disabled={busy || Number(gameState.streetInventory?.beer || 0) <= 0} onClick={actions.kenGiveWifeBeer} detail="Costs one beer; may improve neighborhood diplomacy">Give Ken's wife a beer</DealButton>
      <DealButton disabled={busy} onClick={() => actions.travel('trailer_park')} detail="Return to the Trailer Park in Mt. Gate" tone="leave">Leave Ken's yard</DealButton>
    </>
  );

  if (screen === 'weed') {
    title = 'Ken Buys Bud';
    copy = `Ken offers ${money(ken.weedOffer)} per gram. Your reputation value bonus is ${Number(ken.repValueBonus || 0) >= 0 ? '+' : ''}${Math.round(Number(ken.repValueBonus || 0) * 100)}%.`;
    choices = <>{budOptions.map((amount) => <DealButton key={amount} disabled={busy} onClick={() => actions.kenSellBud(amount)} detail={`Receive ${money(amount * Number(ken.weedOffer || 0))}`}>Sell {grams(amount)}</DealButton>)}{budOptions.length === 0 && <p className="ken-empty">You do not have enough carried bud, or Ken has filled today's demand.</p>}<DealButton disabled={busy} onClick={() => setScreen('main')}>Back</DealButton></>;
  } else if (screen === 'meth') {
    title = 'Ken’s Cheap Meth';
    copy = unlocked ? `Base pricing has been adjusted by a ${Math.round(Number(ken.repDiscount || 0) * 100)}% reputation discount. Ken has ${grams(ken.methStock)} left today.` : `Ken wants at least ${ken.repRequirement || 70} reputation before he shows you the cheap stuff. You currently have ${rep}.`;
    choices = <>{unlocked && methOptions.map((amount) => <DealButton key={amount} disabled={busy || cash < amount * Number(ken.methPrice || 0)} onClick={() => actions.kenBuyMeth(amount)} detail={`Cost ${money(amount * Number(ken.methPrice || 0))}`}>Buy {grams(amount)}</DealButton>)}<DealButton disabled={busy} onClick={() => setScreen('main')}>Back</DealButton></>;
  } else if (screen === 'junk') {
    title = 'Junk for Meth';
    copy = `Ken values junk at a reputation-adjusted ${Math.round(Number(ken.junkFactor || 0) * 100)}% of pawn reference value, then pays in meth.`;
    choices = <>{junk.map(([item, count]) => <DealButton key={item} disabled={busy || Number(ken.methStock || 0) <= 0} onClick={() => actions.kenTradeJunk(item)} detail={`${count} owned · pawn reference ${money(BARTER_PRICES[item] || 0)}`}>Trade {item.replaceAll('_', ' ')}</DealButton>)}<DealButton disabled={busy} onClick={() => setScreen('main')}>Back</DealButton></>;
  }

  return (
    <div className="ken-overlay" role="dialog" aria-modal="true" aria-labelledby="ken-title">
      <div className="ken-frame">
        <div className="ken-art-wrap">
          <img className="ken-art" src="./scenes/kens-house.webp" alt="Ken the Tweaker's run-down house and junk-filled yard in the boonies" />
          <div className="ken-art-shade" aria-hidden="true" />
          <div className="ken-label">THE BOONIES · SPECIALTY DEALER</div>
          <div className="ken-wallet">CASH <strong>{money(cash)}</strong> · REP <strong>{rep}</strong></div>
        </div>
        <div className="ken-lower">
          <section className="ken-copy">
            <span className="scene-kicker">RURAL COMMERCE</span>
            <h2 id="ken-title">{title}</h2>
            <p>{copy}</p>
            {ken.wifeFlavor && screen === 'main' && <blockquote>{ken.wifeFlavor}</blockquote>}
          </section>
          <section className="ken-choices">{choices}</section>
        </div>
        {busy && <div className="ken-busy">KEN IS DOING MATH ON A CIGARETTE CARTON…</div>}
      </div>
    </div>
  );
}
